<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Gifts
            </h2>
            <div class="subtitle is-2">
              <div v-html="giftsText ? giftsText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Gifts</strong> to report.
            </checkbox-adv>
          </div>
        </div>
        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-gifts"
            tag="form"
          >
            <div class="columns">
              <div class="column is-4">
                <dropdown-vee
                  id="gift-type"
                  v-model="newItem.type"
                  :options="giftTypesDropdown"
                  placeholder="Gift Type"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="source"
                  v-model="newItem.source"
                  placeholder="Gift Source Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="value"
                  v-model="newItem.value"
                  type="number"
                  placeholder="Value (xxx.xx)"
                  rules="decimal|required"
                  required
                />
              </div>
            </div>
            <div class="columns">
              <div class="column is-6">
                <textbox-vee
                  id="description"
                  v-model="newItem.description"
                  placeholder="Gift Description"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-6">
                <textbox-vee
                  id="reason"
                  v-model="newItem.reason"
                  placeholder="Gift Reason"
                  rules="required"
                  required
                />
              </div>
            </div>
            <div class="columns">
              <div class="column is-4">
                <textbox
                  id="street-address"
                  v-model="newItem.streetAddress"
                  placeholder="Street Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="zip-code"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
            </div>
            <div class="column is-12 has-text-centered">
              <button
                id="action-btn"
                type="button"
                class="button is-secondary"
                @click="trigerNewItemAction"
              >
                {{ tableAction }} Gift
              </button>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="type"
                  slot-scope="props"
                >
                  {{ getControlValueDesc(props.row.type, 'giftTypes') }}
                </div>
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
              </v-client-table>
              <br>
              <div class="message is-info">
                <div class="message-body">
                  <i>*Values are needed for calculation purposes. Not all amounts will be displayed to the public.</i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Gifts')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'Gifts',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    DropdownVee,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      currentFiling: {},
      items: [],
      isSaving: false,
      newItem: {
        type: '',
        source: '',
        value: '',
        description: '',
        reason: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        person: '',
      },
      nothingToReport: false,
      tableOptions: {
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          value: "Value ($) *",
        },
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
      },
      table: [
        'type',
        'value',
        'description',
        'edit',
        'clone',
        'delete',
      ],
    };
  },
  computed: {
    giftTypes () {
      return this.$store.getters[ 'admin/activeControlValues' ]('gifts');
    },
    giftTypesDropdown () {
      return this.controlValueDropdown('gifts');
    },
    states () {
      return this.controlValueDropdown('state');
    },
    giftsText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('giftsText');
    },
    familyRelationships () {
      return this.controlValueDropdown('familymember');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentGifts();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'giftsText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'gifts'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'familymember'));
      promises.push(this.$store.dispatch('gifts/fetchGifts'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.giftsCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentGifts () {
      return JSON.parse(JSON.stringify(this.$store.state.gifts.gifts));
    },
    saveAndContinue () {
      let promises = [];

      const col = 'giftsCompleted';
      const endpoint = 'gifts';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      //V-model.number won't work here, so we need to parse it ourselves
      this.items.forEach(item => {
        item.value = parseFloat(item.value);
      });

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-gifts'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
